// use-push-permission — статус Notification permission + helper request().
//
// Permission Triple: 'default' (ещё не запрашивали) / 'granted' / 'denied'.
// Используется в PushPermissionPrompt и settings/notifications, чтобы знать,
// можно ли вызывать use-web-push.subscribe().
import { useCallback, useEffect, useState } from "react";

export type PushPermission = NotificationPermission | "unsupported";

function readPermission(): PushPermission {
	if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
	return Notification.permission;
}

export function usePushPermission() {
	const [permission, setPermission] = useState<PushPermission>(readPermission);

	useEffect(() => {
		if (typeof navigator === "undefined" || !("permissions" in navigator)) return;
		let cancelled = false;
		let status: PermissionStatus | null = null;
		let handler: (() => void) | null = null;

		navigator.permissions
			.query({ name: "notifications" as PermissionName })
			.then((s) => {
				if (cancelled) return;
				status = s;
				handler = () => setPermission(s.state === "prompt" ? "default" : s.state);
				handler();
				s.addEventListener("change", handler);
			})
			.catch(() => {
				/* permissions API не поддерживает 'notifications' в WebKit — игнорируем */
			});

		return () => {
			cancelled = true;
			if (status && handler) {
				// addEventListener → removeEventListener (не status.onchange = null,
				// иначе listener остаётся прикреплённым — memory leak).
				status.removeEventListener("change", handler);
			}
		};
	}, []);

	const request = useCallback(async (): Promise<PushPermission> => {
		if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
		const result = await Notification.requestPermission();
		setPermission(result);
		return result;
	}, []);

	return { permission, request };
}
